import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';

import { MainErrorFallback } from '@/components/errors/main';
import { Notifications } from '@/components/ui/notifications';
import { PageLoader } from '@/components/ui/pageloader';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useErrorReporter } from '@/hooks/use-error-reporter';
import { queryConfig } from '@/lib/react-query';

type AppProviderProps = {
  children: React.ReactNode;
};

const AppProvider = ({ children }: AppProviderProps) => {
  const { report } = useErrorReporter();
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: queryConfig,
        queryCache: new QueryCache({
          onError: (e, query) => {
            if (query.meta?.skipGlobalError) {
              return;
            }
            report(e, 10000);
          },
        }),
      }),
  );

  return (
    <React.Suspense fallback={<PageLoader show />}>
      <ErrorBoundary FallbackComponent={MainErrorFallback}>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            {/* Enable in development mode only */}
            {import.meta.env.DEV && <ReactQueryDevtools />}

            <TooltipProvider>{children}</TooltipProvider>

            <Notifications position="top-right" max={5} />
          </QueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
};
AppProvider.displayName = 'AppProvider';

export { AppProvider };
